import * as React from "react";
import { TooltipHost, Stack, IStackTokens, TagPicker, ITag, IBasePickerSuggestionsProps, Label, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { DatasourceService } from "../../services/DatasourceService";
import { connect } from "react-redux";
import { DataSourcePanel } from "./datasourcePanel";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { setDatasourceValues } from "../../redux/modules/datasource";
import { CMAddDatasourceItemPanel } from "../../../../contentManager/Panels/CMAddDatasourceItemPanel";
import { sortTerms } from "../helpers/tagHelper";
import { dataSourceItem } from "../../redux/models/reduxModels";

const stackTokens: IStackTokens = {
    childrenGap: 5
};

const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
        return false;
    }
    return tagList.some(compareTag => compareTag.key === tag.key);
};

const getTextFromItem = (item: ITag) => item.name;

const mapDispatchToProps = {
    setDatasourceValues,
};

type reduxDispatch = typeof mapDispatchToProps;

interface ContentTypeNameDatasourcePickerProps extends reduxDispatch {
    contentTypeInputName: string;
    contentTypeNameValue: string;
    datasourceItem: dataSourceItem;
    updateContentTypeNameValue: (newValue: string) => void;
}

export const UnconnectedContentTypeNameDatasourcePicker: React.FunctionComponent<ContentTypeNameDatasourcePickerProps> = ({
    contentTypeInputName,
    contentTypeNameValue,
    datasourceItem,
    updateContentTypeNameValue,
    setDatasourceValues
}: ContentTypeNameDatasourcePickerProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
    const [boundedField, setBoundedField] = React.useState<string>("");
    const dataSourceService = new DatasourceService();
    const dataSourceHelper = new DatasourceHelper(dataSourceService);
    const [selectedTags, setSelectedTags] = React.useState<ITag[]>([]); //selected tags
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [enableAutoFillRelatedFields, setEnableAutoFillRelatedFields] = React.useState<boolean>(false);
    const [isAddDatasourceItemPanelOpen, setIsAddDatasourceItemPanelOpen] = React.useState(false);

    React.useEffect(() => {
        if (datasourceItem) {
            (async () => {
                setIsLoading(true);
                try {
                    const result = await dataSourceHelper.getSearchStructure(datasourceItem);
                    if (Array.isArray(result)) {
                        if (result.length > 1) setEnableAutoFillRelatedFields(true);
                        setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
                    }
                    const boundedFieldName = DatasourceHelper.getBoundedField(datasourceItem, contentTypeInputName);
                    setBoundedField(boundedFieldName ?? "");
                } catch (error) {
                    logger(error, "contentTypeDatasourcePicker.tsx (useEffect 1)");
                }
                setIsLoading(false);
            })();
        }
    }, [datasourceItem]);

    const isFirstRun = React.useRef(true);
    React.useEffect(() => {
        try {
            const newSelectedTag: ITag = contentTypeNameValue
                ? {
                    name: contentTypeNameValue as string,
                    key: contentTypeNameValue as string,
                }
                : null;

            setSelectedTags(newSelectedTag ? [newSelectedTag] : []);

            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }

            updateSearch(newSelectedTag?.key as string ?? "");
        } catch (e) {
            logger(e, "contentTypeDatasourcePicker.tsx (useEffect 2)");
        }
    }, [contentTypeNameValue]);

    const filterSuggestedTags = async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        let suggestedTags: ITag[] = [];
        if (filterText?.trim()) {
            try {
                const request: IDatasourceQueryDto = {
                    id: datasourceItem.id,
                    name: datasourceItem.name,
                    tenantId: datasourceItem.tenantId,
                    typeId: datasourceItem.type.id,
                    typeName: datasourceItem.type.name,
                    queryParams: [{ name: boundedField, value: filterText.trim() }],
                    returnOnlyQueriedProperties: true,
                    allowCache: datasourceItem.allowCache
                };
                const result = await dataSourceService.Search(request);
                if (result.length > 0) {
                    const boundFieldProperty: string = Object.keys(result[0]).find(key => key.toLowerCase().trim() === boundedField.toLowerCase().trim());
                    if (boundFieldProperty) {
                        suggestedTags = result
                            .map<ITag>(resultRow => {
                                const propertyValue = resultRow[boundFieldProperty]?.trim();
                                return {
                                    name: propertyValue,
                                    key: propertyValue
                                }
                            })
                            .filter((tag, index, tags) => {
                                const tagName: string = tag?.name?.toLowerCase();
                                return tagName?.length > 0
                                    && tagName.indexOf(filterText.trim().toLowerCase()) !== -1
                                    && tags.findIndex(t => (t.name === tag.name)) === index
                                    && !listContainsTagList(tag, tagList)
                            });
                    }
                } else {
                    if (datasourceItem.allowAdd) {
                        suggestedTags.push({
                            name: "Add new item",
                            key: "addNewItem"
                        })
                    }
                }
            } catch (error) {
                logger(error, "datasourceTextTagPicker.tsx (filterSuggestedTags)");
            }
        }
        return sortTerms(filterText, suggestedTags);
    };

    const closePanel = () => {
        setIsOpen(false);
    };

    const updateInputValue = (newValue: string) => {
        updateContentTypeNameValue(newValue);
        updateSearch(newValue);
    };

    const updateSearchInputs = (index: number, value: string) => {
        DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
    };

    const openPanel = async () => {
        updateSearch(contentTypeNameValue);
        setIsOpen(true);
    };

    const updateSearch = (newValue: string) => {
        const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
        updateSearchInputs(index, newValue);
    };

    const onChangeHandle = (tags: ITag[]): void => {
        const searchValue = tags?.length === 1 ? tags[0].key as string : "";
        setSelectedTags(tags);
        updateSearch(searchValue);
        updateContentTypeNameValue(searchValue);
        if (enableAutoFillRelatedFields) getRelatedDatasourceValues(searchValue);
    };

    const getRelatedDatasourceValues = async (searchValue: string) => {
        if (searchValue?.trim()) {
            try {
                const request: IDatasourceQueryDto = {
                    id: datasourceItem.id,
                    name: datasourceItem.name,
                    tenantId: datasourceItem.tenantId,
                    typeId: datasourceItem.type.id,
                    typeName: datasourceItem.type.name,
                    queryParams: [{ name: boundedField, value: searchValue.trim() }],
                    returnOnlyQueriedProperties: false,
                    allowCache: datasourceItem.allowCache
                };

                const result = await dataSourceService.Search(request);
                if (result.length === 1) {
                    const propertyKeys = Object.keys(result[0]); //keys of the data source, not the sharepoint columns
                    if (propertyKeys) {
                        let resultItem: any = result[0];
                        if (resultItem) {
                            let boundObject = {};
                            propertyKeys
                                .forEach((key) => {
                                    if (resultItem[key] != null) {
                                        const boundField = datasourceItem?.boundFields?.find(field => {
                                            return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
                                        }); //check if any of the data source fields have a mapping.
                                        if (boundField) { //if a mapping exists
                                            const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
                                            names
                                                .filter(name => name.toLowerCase().trim() !== contentTypeInputName.toLowerCase().trim())
                                                .forEach(name => {
                                                    boundObject[name] = resultItem[key];
                                                })
                                        } else {
                                            boundObject[key] = resultItem[key];
                                        }
                                    }
                                });

                            setDatasourceValues(boundObject); //redux.    
                        }
                    }
                }
            } catch (error) {
                logger(error, "contentTypeDatasourcePicker.tsx (getRelatedDatasourceValues)");
            }
        }
    }

    const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
        suggestionsHeaderText: languageStrings.Suggestions,
        //noResultsFoundText: showLink ? "No results found, add a new item?" : languageStrings.NoResultsFound
    };

    const onItemSelected = (item) => {
        if (item && item.key === 'addNewItem') {
            setIsAddDatasourceItemPanelOpen(true);
            return null; // return null to prevent adding it to the selected items.
        }
        return item;
    };

    const input = (
        <>
            <Label required>{languageStrings.Name}</Label>
            <TagPicker
                className="xb-sp-input"
                removeButtonAriaLabel="Remove"
                selectedItems={selectedTags}
                onResolveSuggestions={filterSuggestedTags}
                resolveDelay={500}
                getTextFromItem={getTextFromItem}
                pickerSuggestionsProps={pickerSuggestionsProps}
                onChange={onChangeHandle}
                itemLimit={1}
                inputProps={{
                    required: true,
                }}
                onItemSelected={onItemSelected}
            />
        </>
    );

    return (
        <>
            <Stack horizontal verticalAlign="start" tokens={stackTokens}>
                <Stack.Item grow={1}>{input}</Stack.Item>
                {<Stack.Item>
                    {isLoading
                        ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
                        : <div className="xb-search-icon-container">
                            <TooltipHost content={`${languageStrings.Search} ${datasourceItem?.name} ${languageStrings.Datasource}`}>
                                <FontIcon iconName="Search" onClick={openPanel} />
                            </TooltipHost>
                        </div>}
                </Stack.Item>}
            </Stack>
            <DataSourcePanel
                displayName={datasourceItem?.name}
                closePanel={closePanel}
                isOpen={isOpen}
                datasourceItem={datasourceItem}
                datasourceService={dataSourceService}
                searchInputs={searchInputs}
                updateSearchInputs={updateSearchInputs}
                updateInputValue={updateInputValue}
                boundedField={boundedField}
                dataSourceHelper={dataSourceHelper}
                dataSourceItem={datasourceItem}
            />
            {isAddDatasourceItemPanelOpen &&
                <CMAddDatasourceItemPanel
                    panelIsOpen={isAddDatasourceItemPanelOpen}
                    closePanel={() => setIsAddDatasourceItemPanelOpen(false)}
                    displayName={datasourceItem?.name}
                    dataSourceHelper={dataSourceHelper}
                    dataSourceItem={datasourceItem}
                />}
        </>
    );
};

export const ContentTypeNameDatasourcePicker = connect(null, mapDispatchToProps)(UnconnectedContentTypeNameDatasourcePicker);